<script lang="ts">
  import { onMount } from "svelte";
  import { _ as t, locale } from "svelte-i18n";
  import { getContainerStyle } from "../../libs/layout";
  import { isEmbedded } from "../../libs/chief-bridge";
  import { bridge, sendDatafinder } from "../lib/bridge";
  import Carousel from "../components/Carousel.svelte";
  import ActionButtons from "../components/ActionButtons.svelte";
  import HowToCard from "../components/HowToCard.svelte";
  import TaskList from "../components/TaskList.svelte";
  import StageSection from "../components/StageSection.svelte";
  import BigAwards from "../components/BigAwards.svelte";
  import MoreActivity from "../components/MoreActivity.svelte";
  import RiskDisclosure from "../components/RiskDisclosure.svelte";
  import RuleButton from "../components/RuleButton.svelte";
  import DepositRuleDialog from "../components/DepositRuleDialog.svelte";
  import TradeRuleDialog from "../components/TradeRuleDialog.svelte";
  import IpoRuleDialog from "../components/IpoRuleDialog.svelte";
  import InviteRuleDialog from "../components/InviteRuleDialog.svelte";
  import OfflineRuleDialog from "../components/OfflineRuleDialog.svelte";
  import LotteryCodeDialog from "../components/LotteryCodeDialog.svelte";
  import ActivityResultDialog from "../components/ActivityResultDialog.svelte";
  import { ANNIVERSARY_ACTIVITY_KEY, LOTTERY_CODE_D5 } from "../lib/constants";
  import {
    enrollActivity,
    getActivityDetail,
    getActivityInfo,
    getEnrollmentStatus,
    redeemQrcode,
    commonh5Url,
    type ActivityDetail,
    type ActivityEnrollmentStatus,
  } from "../lib/api";
  import bannerCn from "../assets/images/cn/banner.png";
  import bannerTc from "../assets/images/tc/banner.png";

  type ResultDialogType =
    | "confirm"
    | "goToApp"
    | "registerSuccess"
    | "redeemSuccess"
    | "redeemIneligible"
    | "qrAlreadyUsed"
    | "userAlreadyRedeemed"
    | "qrInvalid"
    | "enrollIneligible"
    | "accountNotOpened"
    | "activityNotInPeriod"
    | "requestFailed";
  type RegisterPhase = "idle" | "checking" | "submitting";
  type RegistrationStatus = "unknown" | "ineligible" | "registered";
  type RegisterButtonState =
    | "active"
    | "notStarted"
    | "ended"
    | "ineligible"
    | "registered";

  let tipKey = $state<string | null>(null);
  let showCodes = $state(false);
  let activity = $state<ActivityDetail | null>(null);
  let resultDialog = $state<ResultDialogType | null>(null);
  let registerPhase = $state<RegisterPhase>("idle");
  let registrationStatus = $state<RegistrationStatus>("unknown");
  /** 独立于任务资格；false 表示未开户或账户已关闭 */
  let accountOpened = $state<boolean | undefined>(undefined);
  let howToSection = $state<HTMLDivElement | null>(null);
  let scrollAnimationFrame: number | null = null;
  const isEmbeddedApp = isEmbedded();
  /** 当前时间，每分钟更新一次，保证活动状态实时反映（开始/结束） */
  let currentTime = $state(Date.now());
  let eligibilityRequest: ReturnType<typeof enrollActivity> | null = null;
  /**
   * 防竞态版本号：每次 startRegistration() 自增。
   * confirmRegistration() await 完成后检查版本，不匹配则说明期间发生了新一轮登记，
   * 直接放弃结果，避免旧 dry-run 结果污染新流程。
   */
  let registrationGeneration = 0;

  // 每分钟刷新 currentTime，保证活动时间窗口判断准确
  onMount(() => {
    const interval = setInterval(() => {
      currentTime = Date.now();
    }, 60_000);
    return () => clearInterval(interval);
  });

  function openTip(key: string) {
    tipKey = key;
  }

  /** 进入规则页：加 rules 参数，由 App 监听 popstate 切换 */
  function handleGoToRules() {
    const url = new URL(window.location.href);
    url.searchParams.set("rules", "true");
    window.history.pushState({}, "", url);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  async function loadActivity() {
    const res = isEmbeddedApp
      ? await getActivityDetail(LOTTERY_CODE_D5)
      : await getActivityInfo(LOTTERY_CODE_D5);
    if (res.code === 0 && res.data) {
      activity = res.data;
    }
  }

  /** 把 enrollment-status 的结果落到按钮状态上 */
  function applyEnrollmentStatus(data: ActivityEnrollmentStatus) {
    // 开户状态与任务资格相互独立，不能用 isEligible 推断是否已开户。
    accountOpened = data.accountOpened;
    registrationStatus = data.registered
      ? "registered"
      : data.isEligible === false
        ? "ineligible"
        : "unknown";
  }

  /** 页面进入时查询独立的活动登记状态，不再依赖活动详情或逐任务判断。 */
  async function loadEnrollmentStatus() {
    if (!isEmbeddedApp) return;
    const res = await getEnrollmentStatus(ANNIVERSARY_ACTIVITY_KEY);
    const data = res.code === 0 ? res.data : undefined;
    if (!data) return;
    applyEnrollmentStatus(data);
  }

  function showRedeemFailure(errorCode?: string) {
    switch (errorCode) {
      case "INVALID_CODE":
        resultDialog = "qrInvalid";
        break;
      case "ALREADY_USED":
        resultDialog = "qrAlreadyUsed";
        break;
      case "USER_ALREADY_REDEEMED":
        resultDialog = "userAlreadyRedeemed";
        break;
      case "ACCOUNT_NOT_OPENED":
        accountOpened = false;
        resultDialog = "accountNotOpened";
        break;
      case "NOT_ELIGIBLE":
        registrationStatus = "ineligible";
        resultDialog = "redeemIneligible";
        break;
      case "ACTIVITY_NOT_IN_PERIOD":
        resultDialog = "activityNotInPeriod";
        break;
      default:
        resultDialog = "requestFailed";
    }
  }

  async function redeemFromUrl(qrcodeToken: string) {
    const res = await redeemQrcode(qrcodeToken, ANNIVERSARY_ACTIVITY_KEY);
    const data = res.data;
    if (data?.registered) registrationStatus = "registered";
    if (res.code === 0 && data?.success) {
      registrationStatus = "registered";
      resultDialog = "redeemSuccess";
      // TODO: partialSuccess=true 时的专属文案和重试行为待接口联调确认。
      return;
    }
    showRedeemFailure(data?.errorCode);
  }

  /** 从 URL 上摘掉核销码，避免刷新页面重复核销 */
  function dropQrcodeTokenFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete("qrcodeToken");
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }

  /**
   * 扫码核销：只用 enrollment-status 判断有没有开户，未开户先引导开户，
   * 其余情况一律走核销流程，资格 / 是否已核销交给核销接口判定
   * （INVALID_CODE / ALREADY_USED / USER_ALREADY_REDEEMED / NOT_ELIGIBLE ...）。
   *
   * 核销成功后端会顺带把活动登记做掉，所以这里成功即视为已登记，不再单独调登记接口。
   *
   * 未开户分支保留 URL 上的 qrcodeToken：用户开完户重新进入页面还能继续核销。
   * 只有确认要调核销接口时才摘掉 token，避免刷新重复核销。
   */
  async function redeemWithPrecheck(qrcodeToken: string) {
    const res = await getEnrollmentStatus(ANNIVERSARY_ACTIVITY_KEY);
    const status = res.code === 0 ? res.data : undefined;

    // 状态拿到就先落到按钮上；拿不到也不拦核销，未开户核销接口会回 ACCOUNT_NOT_OPENED。
    if (status) {
      applyEnrollmentStatus(status);
      if (status.accountOpened === false) {
        resultDialog = "accountNotOpened";
        return;
      }
    }

    dropQrcodeTokenFromUrl();
    await redeemFromUrl(qrcodeToken);
  }

  async function initializePage() {
    const qrcodeToken = new URL(window.location.href).searchParams
      .get("qrcodeToken")
      ?.trim();

    // App 内扫码进入：判断开户后直接核销（核销成功后端会同时完成活动登记），
    // 之后再拉 D5 详情刷新抽奖码数量。
    if (isEmbeddedApp && qrcodeToken) {
      await redeemWithPrecheck(qrcodeToken);
      await loadActivity();
      return;
    }

    // 站外扫码进入：没有登录态，弹窗提示去 App
    if (!isEmbeddedApp && qrcodeToken) {
      await loadActivity();
      resultDialog = "goToApp";
      return;
    }

    // 普通进入（无核销码）：照常加载活动详情；只有 App 内才会查询登记状态。
    await Promise.all([loadActivity(), loadEnrollmentStatus()]);
  }

  function scrollToHowToCard() {
    if (!howToSection) return;
    const scrollContainer = howToSection.closest(
      ".nav-content",
    ) as HTMLElement | null;
    if (!scrollContainer) {
      howToSection.scrollIntoView();
      return;
    }

    const startTop = scrollContainer.scrollTop;
    const navBarHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--nav-bar-height",
        ),
      ) || 0;
    const targetTop = Math.max(
      0,
      startTop +
        howToSection.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top -
        navBarHeight -
        12,
    );

    if (scrollAnimationFrame !== null) {
      cancelAnimationFrame(scrollAnimationFrame);
    }
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      scrollContainer.scrollTop = targetTop;
      return;
    }

    const startedAt = performance.now();
    const duration = 400;
    const distance = targetTop - startTop;
    const animate = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      scrollContainer.scrollTop = startTop + distance * eased;
      if (progress < 1) {
        scrollAnimationFrame = requestAnimationFrame(animate);
      } else {
        scrollAnimationFrame = null;
      }
    };
    scrollAnimationFrame = requestAnimationFrame(animate);
  }

  function startRegistration() {
    trackRegisterClick();
    if (registerPhase !== "idle") return;
    if (registerButtonState === "registered") {
      scrollToHowToCard();
      return;
    }
    // 未开始 / 已结束 / 不符合条件：按钮置灰禁用，点击不做任何响应
    if (registerButtonState !== "active") return;
    // 站外没有登录态，唤起 App
    if (!isEmbeddedApp) {
      goToApp();
      return;
    }
    // 开户状态与 isEligible 独立；明确未开户时直接提示，不调用登记接口。
    if (accountOpened === false) {
      resultDialog = "accountNotOpened";
      return;
    }
    resultDialog = "confirm";
    registerPhase = "checking";
    // 展示确认弹窗的同时，仅校验资格，不写库，也不传 requestId。
    // 同步自增版本号，用于 confirmRegistration 的竞态检测。
    registrationGeneration += 1;
    eligibilityRequest = enrollActivity(ANNIVERSARY_ACTIVITY_KEY, true);
  }

  function showEnrollFailure(errorCode?: string) {
    switch (errorCode) {
      case "ACCOUNT_NOT_OPENED":
        accountOpened = false;
        resultDialog = "accountNotOpened";
        break;
      case "NOT_ELIGIBLE":
        registrationStatus = "ineligible";
        resultDialog = "enrollIneligible";
        break;
      case "NOT_IN_PERIOD":
        resultDialog = "activityNotInPeriod";
        break;
      default:
        resultDialog = "requestFailed";
    }
  }

  async function confirmRegistration() {
    if (registerPhase === "submitting") return;
    // 防御性检查：确保 eligibilityRequest 已被 startRegistration 赋值
    if (!eligibilityRequest) {
      registerPhase = "idle";
      resultDialog = "requestFailed";
      return;
    }
    registerPhase = "submitting";
    // 记录当前版本号，await 完成后用于检测是否发生了竞态（弹窗关闭后重新触发登记）
    const gen = registrationGeneration;

    const eligibility = await eligibilityRequest;

    // 竞态检测：如果版本号已变，说明期间有新一轮登记启动，放弃本次结果
    if (gen !== registrationGeneration) {
      registerPhase = "idle";
      return;
    }

    const eligibilityData = eligibility?.data;
    // dry-run 模式下 registered: false 表示"用户尚未登记，但有资格可以登记"，是合法状态；
    // 只有接口调用失败、code 非 0、或返回 errorCode 才视为资格校验失败。
    if (
      !eligibility ||
      eligibility.code !== 0 ||
      eligibilityData?.errorCode
    ) {
      registerPhase = "idle";
      eligibilityRequest = null;
      showEnrollFailure(eligibilityData?.errorCode);
      return;
    }

    // dry-run 通过后正式登记。TODO: 后端返回结构明确后补 requestId。
    const result = await enrollActivity(ANNIVERSARY_ACTIVITY_KEY, false);
    const resultData = result.data;
    registerPhase = "idle";
    eligibilityRequest = null;
    if (
      result.code === 0 &&
      !resultData?.errorCode &&
      resultData?.registered !== false
    ) {
      registrationStatus = "registered";
      resultDialog = "registerSuccess";
      await loadActivity();
      return;
    }
    showEnrollFailure(resultData?.errorCode);
  }

  function closeResultDialog() {
    resultDialog = null;
    registerPhase = "idle";
    eligibilityRequest = null;
    // 自增版本号，使任何正在进行中的 confirmRegistration 竞态检测失效，
    // 防止弹窗关闭后旧 dry-run 结果返回时干扰已重置的状态。
    registrationGeneration += 1;
  }

  /** 确认弹窗的确认键走登记；未开户弹窗确认键跳开户；goToApp 弹窗确认键跳 App；其余只关闭。 */
  async function handleResultConfirm() {
    if (resultDialog === "confirm") {
      await confirmRegistration();
      return;
    }
    if (resultDialog === "goToApp") {
      closeResultDialog();
      goToApp();
      return;
    }
    if (resultDialog === "accountNotOpened") {
      closeResultDialog();
      // 与任务列表「去开户」共用同一入口：iOS 走 WebView 路由，Android 使用 linkType 1。
      goOpenAccount();
      return;
    }
    closeResultDialog();
  }

  // ============ 埋点 ============

  const TRACK_PAGE_NAME = "8月抽奖";
  const TRACK_H5_TITLE = "8月抽奖";
  const TRACK_P_ID = "lottery_anniversary";
  const TRACK_M_ID = "d5";

  /** from_channel 为选填，URL 上没有就不上报 */
  function trackChannel(): { from_channel?: string } {
    const channel = new URLSearchParams(window.location.search).get(
      "from_channel",
    );
    return channel ? { from_channel: channel } : {};
  }

  /** 落地页浏览 */
  function trackPageView() {
    sendDatafinder("page_view", {
      page_name: TRACK_PAGE_NAME,
      scenes: "8月抽奖浏览",
      H5_title: TRACK_H5_TITLE,
      H5_url: window.location.href,
      p_id: TRACK_P_ID,
      m_id: TRACK_M_ID,
      t_id: "w",
      s_id: "w",
      e_id: "w",
      ...trackChannel(),
    });
  }

  /** 任务埋点名称固定用简体中文，不随页面语言变化 */
  const TRACK_TASK_NAMES: Record<string, string> = {
    open_account: "开户抽奖",
    deposit: "入金任务",
    trade: "交易任务",
    ipo: "新股认购任务",
    invite: "邀请好友任务",
    offline: "线下活动任务",
  };

  /** 任务按钮点击，e_id 传任务简体中文名称 */
  function trackTaskClick(taskKey: string) {
    sendDatafinder("element_click", {
      page_name: TRACK_PAGE_NAME,
      scenes: "抽奖落地页点击",
      element_name: "任务点击",
      H5_title: TRACK_H5_TITLE,
      p_id: TRACK_P_ID,
      m_id: TRACK_M_ID,
      t_id: "w",
      s_id: "w",
      e_id: TRACK_TASK_NAMES[taskKey] ?? taskKey,
      ...trackChannel(),
    });
  }

  /** 立即登记点击 */
  function trackRegisterClick() {
    sendDatafinder("element_click", {
      page_name: TRACK_PAGE_NAME,
      scenes: "8月抽奖报名点击",
      element_name: "立即登记",
      H5_title: TRACK_H5_TITLE,
      p_id: TRACK_P_ID,
      m_id: TRACK_M_ID,
      t_id: "w",
      s_id: "w",
      e_id: "register",
      ...trackChannel(),
    });
  }

  onMount(() => {
    trackPageView();
    void initializePage();
  });

  // ============ 站外唤起 App / 站内任务跳转 ============

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  const openAccountUrl =
    __MODE__ === "prod"
      ? "https://service.chiefgroup.com.hk"
      : "https://chief-fe-webapp.test.stx365.com";
  const depositUrl =
    __MODE__ === "prod"
      ? "https://chief-deposit.chiefgroup.com.hk/cash/money/type"
      : "https://chief-deposit.test.stx365.com/cash/money/type";
  const ipoUrl =
    __MODE__ === "prod"
      ? "https://ipo-h5.chiefgroup.com.hk/ipo/index/1"
      : "https://ipo-h5.test.stx365.com/ipo/index/1";
  const inviteUrl = `${commonh5Url}invite-friends-h5/`;
  const anniversaryActivityBaseUrl =
    __MODE__ === "prod"
      ? "https://common-h5.chiefgroup.com.hk/anniversary-activity"
      : "https://common-h5.test.stx365.com/anniversary-activity";
  /** 更多活动：感恩赏活动页（commonh5Url 自带结尾斜杠） */
  const gratitudeRewardsUrl = `${commonh5Url}gratitude-rewards-h5`;
  /** 活动中心 H5（Android WebView / 站外浏览器兜底） */
  const activityCenterUrl =
    __MODE__ === "prod"
      ? "https://chief-deposit.chiefgroup.com.hk/activityCenter"
      : "https://chief-deposit.test.stx365.com/activityCenter";

  /** 当前页有 from_channel 时带给目标页，用于渠道归因 */
  function withFromChannel(baseUrl: string): string {
    const targetUrl = new URL(baseUrl);
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has("from_channel")) {
      targetUrl.searchParams.set(
        "from_channel",
        currentUrl.searchParams.get("from_channel") ?? "",
      );
    }
    return targetUrl.toString();
  }

  function getAnniversaryActivityUrl(): string {
    return withFromChannel(anniversaryActivityBaseUrl);
  }

  const APP_DOWNLOAD_URL = "https://official.chiefgroup.com.hk/chief-download";

  /** 通过原生 scheme 跳转（iOS / Android 路由不同） */
  function navigateInApp(iosUrl: string, androidUrl: string) {
    bridge.send("openLink", {
      linkType: 3,
      linkUrl: isIOS ? iosUrl : androidUrl,
    });
  }

  function androidWebRoute(title: string, url: string, type = "Other") {
    return `com.app.ui.nav.HomeScreen$WebViewCompose@${JSON.stringify({
      showTitle: title,
      webType: type,
      url,
    })}`;
  }

  /** 站外：尝试唤起 App，失败则跳下载页 */
  function goToApp() {
    if (!isMobile) {
      window.open(APP_DOWNLOAD_URL, "_blank");
      return;
    }
    const pageUrl = window.location.href;
    const schemeUrl = isIOS
      ? `chiefAppNav://web?url=${encodeURIComponent(pageUrl)}&displayNav=0`
      : `chief://service.chiefgroup.com.hk/dp?isLogin=true&route=${encodeURIComponent(
          androidWebRoute("", pageUrl),
        )}`;

    let hasLeft = false;
    const onVisibilityChange = () => {
      if (document.hidden) hasLeft = true;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.location.href = schemeUrl;
    setTimeout(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (!hasLeft) window.location.href = APP_DOWNLOAD_URL;
    }, 1500);
  }

  /** 活动中心：站内走原生 scheme，站外唤起 App，未安装则跳下载页 */
  function goActivityCenter() {
    if (!isEmbeddedApp) {
      if (!isMobile) {
        window.open(APP_DOWNLOAD_URL, "_blank");
        return;
      }
      const schemeUrl = isIOS
        ? "chiefAppNav://activityCenter"
        : `chief://service.chiefgroup.com.hk/dp?isLogin=true&route=${encodeURIComponent(
            androidWebRoute("", activityCenterUrl, "ActivityCenter"),
          )}`;
      let hasLeft = false;
      const onVisibilityChange = () => {
        if (document.hidden) hasLeft = true;
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.location.href = schemeUrl;
      setTimeout(() => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        if (!hasLeft) window.location.href = APP_DOWNLOAD_URL;
      }, 1500);
      return;
    }
    navigateInApp(
      "chiefAppNav://activityCenter",
      androidWebRoute("", activityCenterUrl, "ActivityCenter"),
    );
  }

  /** 主会场按钮：跳活动中心 */
  function goMainHall() {
    goActivityCenter();
  }

  /**
   * 更多活动图：站内走 bridge openLink，站外直接跳链接，有 from_channel 则带上。
   * 标题传空串：目标页自己会用 Send_Title_To_App 上报标题，
   * 这里不能沿用 $t("title")，那是本页（47 周年抽奖）的标题。
   */
  function goGratitudeRewards() {
    const targetUrl = withFromChannel(gratitudeRewardsUrl);
    if (!isEmbeddedApp) {
      window.location.href = targetUrl;
      return;
    }
    navigateInApp(
      `chiefAppNav://web?url=${encodeURIComponent(targetUrl)}&displayNav=0&displayAllWebviewToH5=1`,
      androidWebRoute("", targetUrl),
    );
  }

  /** 去开户 */
  function goOpenAccount() {
    const title = $t("btn_open_account");
    if (isIOS) {
      bridge.send("openLink", {
        linkType: 3,
        linkUrl: `chiefAppNav://web?url=${encodeURIComponent(openAccountUrl)}&title=${encodeURIComponent(title)}&displayNav=0&displayAllWebviewToH5=1`,
      });
      return;
    }
    bridge.send("openLink", { linkType: 1, linkUrl: openAccountUrl });
  }

  /**
   * 任务按钮点击：
   * - 站外：唤起 / 下载 App
   * - 站内開戶抽獎：跳开户
   * - 站内未登记：先走活动登记流程
   * - 站内已登记：跳各任务对应页面
   */
  function handleTaskAction(key: string) {
    trackTaskClick(key);
    if (key === "offline") {
      const anniversaryActivityUrl = getAnniversaryActivityUrl();
      if (isEmbeddedApp) {
        const title = $t("task_offline_title");
        navigateInApp(
          `chiefAppNav://web?url=${encodeURIComponent(anniversaryActivityUrl)}&title=${encodeURIComponent(title)}&displayNav=0&displayAllWebviewToH5=1`,
          androidWebRoute(title, anniversaryActivityUrl),
        );
      } else {
        window.location.href = anniversaryActivityUrl;
      }
      return;
    }
    if (!isEmbeddedApp) {
      goToApp();
      return;
    }
    if (key === "open_account") {
      goOpenAccount();
      return;
    }
    // 开户状态优先：未开户时统一引导开户，避免提示「不符合報名資格」误导用户
    if (accountOpened === false) {
      resultDialog = "accountNotOpened";
      return;
    }
    // 不符合资格：按钮已置灰禁用，走不到这里；兜底也不做任何提示
    if (registrationStatus === "ineligible") return;
    if (registrationStatus !== "registered") {
      startRegistration();
      return;
    }
    switch (key) {
      case "deposit":
        navigateInApp(
          "chiefAppNav://deposit",
          androidWebRoute($t("task_deposit_title"), depositUrl),
        );
        break;
      case "trade":
        navigateInApp(
          "chiefAppNav://market",
          "com.app.ui.nav.MarketScreen$MarketMain@{}",
        );
        break;
      case "ipo":
        navigateInApp(
          `chiefAppNav://ipo?url=${encodeURIComponent(ipoUrl)}`,
          androidWebRoute($t("task_ipo_title"), ipoUrl),
        );
        break;
      case "invite":
        navigateInApp(
          "chiefAppNav://inviteFriends",
          androidWebRoute($t("task_invite_title"), inviteUrl, "Referral"),
        );
        break;
    }
  }

  /** 将接口的 YYYY-MM-DD HH:mm:ss 按本地时间解析，避免旧浏览器字符串解析差异。 */
  function parseActivityTime(dateStr?: string): number | null {
    if (!dateStr) return null;
    const match = dateStr.match(
      /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2}):(\d{1,2}))?/,
    );
    if (!match) return null;
    const [, year, month, day, hour = "0", minute = "0", second = "0"] = match;
    const timestamp = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  /** 活动时间：接口格式为 YYYY-MM-DD HH:mm:ss，仅展示 YYYY/M/D */
  function formatDate(dateStr?: string): string {
    if (!dateStr) return "";
    const match = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!match) return dateStr;
    const [, year, month, day] = match;
    return `${year}/${Number(month)}/${Number(day)}`;
  }

  let registerButtonState: RegisterButtonState = $derived.by(() => {
    const activityFrom = parseActivityTime(activity?.activityFrom);
    const activityTo = parseActivityTime(activity?.activityTo);
    if (activityFrom !== null && currentTime < activityFrom)
      return "notStarted";
    if (activityTo !== null && currentTime >= activityTo) return "ended";
    if (registrationStatus === "registered") return "registered";
    /*
     * 开户状态优先于资格：未开户时按钮保持点亮、文案仍是「立即登記」，
     * 点击走 startRegistration 弹「請先開戶後再參加活動」引导开户。
     * 不能置灰成「不符合條件」——接口对未开户用户也会返回 isEligible=false，
     * 一置灰 startRegistration 就被 `!== "active"` 拦掉，用户拿不到去开户的引导。
     */
    if (accountOpened === false) return "active";
    if (registrationStatus === "ineligible") return "ineligible";
    return "active";
  });

  let activityPeriod = $derived(
    activity
      ? `${formatDate(activity.activityFrom)} - ${formatDate(activity.activityTo)}`
      : "",
  );

  /** 简体用 cn 图，其余（繁体 / 英文 / 韩文 / 站外默认）用 tc 图 */
  let bannerImg = $derived($locale === "zh-cn" ? bannerCn : bannerTc);

  /** 吸底三个按钮：滚过一屏才出现（banner 里已经有一份同样的按钮，这是滚动后的常驻入口） */
  let showStickyBar = $state(false);
  let pageRoot: HTMLElement | undefined = $state();

  /*
   * 滚动发生在 NavBar 的内容区（.nav-content），不是 window，
   * 所以往上找这个祖先节点监听；找不到就退回 window（布局变化时的兜底）。
   */
  onMount(() => {
    const host = pageRoot?.closest(".nav-content") as HTMLElement | null;
    const update = () => {
      const scrollTop = host ? host.scrollTop : window.pageYOffset;
      showStickyBar = scrollTop > window.innerHeight;
    };
    update();
    const target: HTMLElement | Window = host ?? window;
    target.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      target.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  });
</script>

<div
  bind:this={pageRoot}
  class="w-full min-h-screen overflow-hidden bg-[#0E2C8C]"
  style={getContainerStyle()}
>
  <div
    class="relative w-full"
    style="background-image: url({bannerImg}); background-repeat: no-repeat; background-position: top center; background-size: 100% auto;"
  >
    <RuleButton onClick={handleGoToRules} />
    <Carousel />
    <ActionButtons
      registerState={registerButtonState}
      onMainVenue={goMainHall}
      onRegister={startRegistration}
      onLotteryCode={() => (showCodes = true)}
    />
  </div>

  <div class="px-[16px] mt-[36px]"><StageSection /></div>
  <div class="mt-[-20px]"><BigAwards /></div>
  <p class="activity-period mt-[16px] text-center">{activityPeriod}</p>
  <div bind:this={howToSection} class="px-[16px] mt-[16px]">
    <HowToCard licenseText={activity?.activityDesc ?? ""}>
      <TaskList
        embedded={isEmbeddedApp}
        registered={registrationStatus === "registered"}
        ineligible={registrationStatus === "ineligible"}
        {accountOpened}
        onTip={openTip}
        onAction={handleTaskAction}
      />
    </HowToCard>
  </div>
  <div class="px-[16px] mt-[16px]">
    <MoreActivity onClick={goGratitudeRewards} />
  </div>
  <!--
    底部留白只需刚好躲开吸底栏：吸底栏 76px + 安全区，多留 12px 呼吸位。
    原来根节点 pb-40 叠上这里的 pb-92 共 132px，滚到底时和吸底栏之间空一大片。
  -->
  <div class="px-[16px] mt-[24px] risk-section">
    <RiskDisclosure />
  </div>
</div>

<!--
  吸底三个按钮：banner 里已经有一份同样的按钮，这份是滚过一屏后常驻出现的入口，
  方便滚到页面下方时不用滚回顶部就能登记/看抽奖码。
  设计稿：高 76px / 背景渐变 #0E2C8C → #2E4CAA / 阴影 0 -2px 15px rgba(0,6,41,.4)。
  高度写在 style 里连同安全区一起算：border-box 下 height 含 padding，
  所以 height = 76 + safe，padding-bottom = safe，内容区正好 76px。
  这里不能加左右 padding：量过切图，两侧按钮 80 + 中间 214 + 两侧 80 = 374 ≈ 375，
  三个按钮本来就是撑满整行宽度的（banner 里那份同样没有横向 padding），
  加了 padding 左边按钮就不贴边、整排也会被挤窄。
  ActionButtons 传 compact：只把整行上偏移从 -35px 换成 -20px，
  三个按钮之间的对齐关系保持和 banner 一致。
  用 opacity + visibility 切换而不是 {#if}，避免反复挂载/卸载抖动。
-->
<footer
  class="sticky-action-bar fixed bottom-0 left-1/2 z-40 w-full -translate-x-1/2 bg-[linear-gradient(180deg,#0E2C8C_0%,#2E4CAA_100%)] shadow-[0_-2px_15px_rgba(0,6,41,0.4)]"
  class:sticky-action-bar--visible={showStickyBar}
  style={isEmbeddedApp ? "" : "max-width: 640px;"}
>
  <ActionButtons
    compact
    registerState={registerButtonState}
    onMainVenue={goMainHall}
    onRegister={startRegistration}
    onLotteryCode={() => (showCodes = true)}
  />
</footer>

<DepositRuleDialog
  show={tipKey === "deposit"}
  onClose={() => (tipKey = null)}
/>
<TradeRuleDialog show={tipKey === "trade"} onClose={() => (tipKey = null)} />
<IpoRuleDialog show={tipKey === "ipo"} onClose={() => (tipKey = null)} />
<InviteRuleDialog show={tipKey === "invite"} onClose={() => (tipKey = null)} />
<OfflineRuleDialog
  show={tipKey === "offline"}
  onClose={() => (tipKey = null)}
/>

<LotteryCodeDialog
  show={showCodes}
  onClose={() => (showCodes = false)}
  count={activity?.poolInfo?.myTicketCount ?? 0}
  tickets={activity?.poolInfo?.userTicketDetail ?? []}
  activityStatus={activity?.status ?? 0}
/>

<ActivityResultDialog
  show={resultDialog !== null}
  type={resultDialog}
  onClose={closeResultDialog}
  onConfirm={handleResultConfirm}
/>

<style>
  /* 活动时间（设计稿）：16px / 800 / line-height 24px / 居中 / #FFFFFF */
  .activity-period {
    font-size: 16px;
    font-weight: 800;
    line-height: 24px;
    letter-spacing: 0;
    color: #ffffff;
  }

  /*
    栏高和底部留白必须写在 CSS 里，不能用内联 style：
    内联 style 不经过 PostCSS，px 不会被转成 rem。而栏里的按钮是 Tailwind 类（已转 rem），
    视口 ≥640px 时 html font-size 封顶在 640/37.5≈17.07px，按钮被放大约 1.7 倍，
    栏高却还是死的 76px，按钮就被挤到视口外面看不见了。
    max-width 留在内联：px2rem 本身跳过 max-width，640px 锁宽不受影响。
  */
  /* 默认隐藏，滚过一屏加 --visible 渐入；visibility 保证隐藏时不可点/不占焦点 */
  .sticky-action-bar {
    height: calc(76px + env(safe-area-inset-bottom, 0px));
    padding-bottom: env(safe-area-inset-bottom, 0px);
    /*
      按钮溢出栏外的部分（登记键下缘的发光、两侧按钮往外挪的 2px）统一裁掉，
      避免有安全区的机型能看到、桌面看不到，两边表现不一致
    */
    overflow: hidden;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .risk-section {
    padding-bottom: calc(95px + env(safe-area-inset-bottom, 0px));
  }

  .sticky-action-bar--visible {
    visibility: visible;
    opacity: 1;
  }
</style>
